/**
 * Configuración de datos del sistema solar
 * Contiene información astronómica real de planetas con datos precisos de la NASA/JPL
 */

// Constantes astronómicas (actualizadas 2025)
export const ASTRONOMICAL_CONSTANTS = {
    AU: 149597870.7, // Unidad Astronómica en km
    LIGHT_YEAR: 9.461e12, // Año luz en km
    PARSEC: 3.0857e13, // Parsec en km (1 parsec = 3.26 años luz)
    SOLAR_MASS: 1.989e30, // kg
    SOLAR_RADIUS: 695700, // km (actualizado 2025)
    SOLAR_GALACTIC_VELOCITY: 220, // km/s
    EARTH_YEAR: 365.256, // días (actualizado 2025)

    // Estructuras del sistema solar exterior
    ASTEROID_BELT: {
        innerRadius: 2.2, // AU - límite interior del cinturón principal
        outerRadius: 3.2, // AU - límite exterior del cinturón principal
        particleCount: 2000, // Número de partículas a mostrar
        thickness: 0.3, // AU - grosor vertical del cinturón
        inclination: 7.0, // grados - inclinación promedio respecto al plano eclíptico
    },

    KUIPER_BELT: {
        innerRadius: 30, // AU - límite interior del cinturón de Kuiper
        outerRadius: 50, // AU - límite exterior del cinturón de Kuiper
        particleCount: 1500, // Número de partículas a mostrar
        thickness: 10, // AU - grosor vertical del cinturón
        inclination: 1.86, // grados - inclinación promedio respecto al plano eclíptico
    },

    OORT_CLOUD: {
        innerRadius: 2000, // AU - límite interior de la nube de Oort
        outerRadius: 100000, // AU - límite exterior de la nube de Oort
        particleCount: 3000, // Número de partículas a mostrar
        spherical: true, // Distribución esférica, no en disco
    },

    // Heliosfera: burbuja de viento solar. Distancias según las medidas de las Voyager.
    HELIOSPHERE: {
        terminationShock: 90, // AU - choque de terminación (el viento solar se vuelve subsónico)
        heliopause: 120, // AU - heliopausa: borde de la heliosfera (V1 ~121 UA 2012, V2 ~119 UA 2018)
    },
};

// Configuración de escalas para visualización 3D
export const SCALE_CONFIG = {
    distanceScale: 1e-9, // Factor de compresión de distancias (legacy)
    sizeScale: 1e-6, // Factor de escala de tamaños (legacy)
    timeScale: 365, // Días por segundo de simulación base
    trailScale: 0.1, // Factor de escala para estelas
    minPlanetSize: 0.5, // Tamaño mínimo visible de planeta
    maxPlanetSize: 5.0, // Tamaño máximo visible de planeta
    // Configuración para tamaños proporcionales realistas
    proportionalSizing: {
        enabled: true,
        baseRadius: 6371.0, // Radio de la Tierra como referencia (km)
        baseSize: 0.1, // Tamaño base pequeño para radios realistas

        // Escalado realista - sin tamaños mínimos artificiales
        realisticMode: true, // Siempre usar proporciones reales

        // Configuración sincronizada de tamaños y distancias
        baseOrbitalRadius: 149598000, // Radio orbital de la Tierra como referencia (km)
        baseOrbitalDistance: 50, // Distancia orbital base reducida para mejor visualización

        // Umbral SOLO de diagnóstico (en unidades de simulación): por debajo de este tamaño
        // los avisos marcan un planeta como "difícil de ver". NO se aplica a los tamaños
        // reales —mantenemos las proporciones realistas—, es solo para validaciones/avisos.
        minVisibleSize: 0.05,

        minOrbitalDistance: 15, // Distancia mínima para planetas muy cercanos
        maxOrbitalDistance: 5000, // Límite máximo para planetas exteriores
    },
};

export const solarSystemData = {
    sun: {
        id: 'sun',
        name: 'Sol',
        radius: ASTRONOMICAL_CONSTANTS.SOLAR_RADIUS, // km
        mass: ASTRONOMICAL_CONSTANTS.SOLAR_MASS, // kg
        galacticVelocity: { x: 0, y: 0, z: ASTRONOMICAL_CONSTANTS.SOLAR_GALACTIC_VELOCITY }, // km/s
        position: { x: 0, y: 0, z: 0 },
        color: 0xfdb813,
        emissiveColor: 0xffaa00,
        emissiveIntensity: 0.4,
        temperature: 5778, // K (temperatura superficial)
    },
    planets: [
        {
            id: 'mercury',
            name: 'Mercurio',
            radius: 2439.7, // km
            mass: 3.301e23, // kg
            orbitalRadius: 57.909e6, // km (0.387 AU) - semieje mayor (actualizado 2025)
            orbitalPeriod: 87.969, // días
            rotationPeriod: 58.646, // días
            orbitalEccentricity: 0.2056,
            orbitalInclination: 7.004, // grados (actualizado 2025)
            color: 0x8c7853,
            trailColor: 0xff6b35,
            albedo: 0.142,
            surfaceGravity: 3.7, // m/s²
            atmosphereComposition: 'Prácticamente inexistente',
            moons: 0,
        },
        {
            id: 'venus',
            name: 'Venus',
            radius: 6051.8, // km
            mass: 4.867e24, // kg
            orbitalRadius: 108.21e6, // km (0.723 AU)
            orbitalPeriod: 224.701, // días
            rotationPeriod: -243.025, // días (rotación retrógrada)
            orbitalEccentricity: 0.0068, // actualizado 2025
            orbitalInclination: 3.395, // grados (actualizado 2025)
            color: 0xffc649,
            trailColor: 0xff8c42,
            albedo: 0.689,
            surfaceGravity: 8.87, // m/s²
            atmosphereComposition: '96.5% CO₂, 3.5% N₂',
            moons: 0,
        },
        {
            id: 'earth',
            name: 'Tierra',
            radius: 6371.0, // km (radio medio)
            mass: 5.972e24, // kg
            orbitalRadius: 149.598e6, // km (1.0 AU exacta)
            orbitalPeriod: ASTRONOMICAL_CONSTANTS.EARTH_YEAR, // días
            rotationPeriod: 0.99726968, // días (23h 56m 4s)
            orbitalEccentricity: 0.0167,
            orbitalInclination: 0.0, // grados (referencia)
            color: 0x6b93d6,
            trailColor: 0x4fc3f7,
            albedo: 0.306,
            surfaceGravity: 9.807, // m/s²
            atmosphereComposition: '78% N₂, 21% O₂, 1% otros',
            moons: 1,
        },
        {
            id: 'mars',
            name: 'Marte',
            radius: 3389.5, // km
            mass: 6.39e23, // kg
            orbitalRadius: 227.956e6, // km (1.524 AU) - actualizado 2025
            orbitalPeriod: 686.98, // días
            rotationPeriod: 1.025957, // días (24h 37m)
            orbitalEccentricity: 0.0935, // actualizado 2025
            orbitalInclination: 1.848, // grados (actualizado 2025)
            color: 0xcd5c5c,
            trailColor: 0xff5722,
            albedo: 0.17,
            surfaceGravity: 3.71, // m/s²
            atmosphereComposition: '95% CO₂, 3% N₂, 2% Ar',
            moons: 2,
        },
        {
            id: 'jupiter',
            name: 'Júpiter',
            radius: 69911, // km (radio ecuatorial)
            mass: 1.898e27, // kg
            orbitalRadius: 778.479e6, // km (5.204 AU) - actualizado 2025
            orbitalPeriod: 4332.589, // días (11.862 años) - actualizado 2025
            rotationPeriod: 0.41354, // días (9h 55m)
            orbitalEccentricity: 0.0487, // actualizado 2025
            orbitalInclination: 1.304, // grados
            color: 0xd8ca9d,
            trailColor: 0xffb74d,
            albedo: 0.343,
            surfaceGravity: 24.79, // m/s²
            atmosphereComposition: '89% H₂, 10% He, 1% otros',
            moons: 95, // conocidas hasta 2023
            hasRings: true,
        },
        {
            id: 'saturn',
            name: 'Saturno',
            radius: 58232, // km (radio ecuatorial)
            mass: 5.683e26, // kg
            orbitalRadius: 1432.041e6, // km (9.573 AU) - actualizado 2025
            orbitalPeriod: 10755.699, // días (29.457 años) - actualizado 2025
            rotationPeriod: 0.44401, // días (10h 39m)
            orbitalEccentricity: 0.052, // actualizado 2025
            orbitalInclination: 2.486, // grados (actualizado 2025)
            color: 0xfad5a5,
            trailColor: 0xffcc02,
            albedo: 0.342,
            surfaceGravity: 10.44, // m/s²
            atmosphereComposition: '96% H₂, 3% He, 1% otros',
            moons: 146, // conocidas hasta 2023
            hasRings: true,
            ringSystem: {
                innerRadius: 74500, // km
                outerRadius: 282000, // km
            },
        },
        {
            id: 'uranus',
            name: 'Urano',
            radius: 25362, // km (radio ecuatorial)
            mass: 8.681e25, // kg
            orbitalRadius: 2867.043e6, // km (19.165 AU) - actualizado 2025
            orbitalPeriod: 30685.4, // días (84.016 años) - actualizado 2025
            rotationPeriod: -0.71833, // días (rotación retrógrada, 17h 14m)
            orbitalEccentricity: 0.0469, // actualizado 2025
            orbitalInclination: 0.77, // grados (actualizado 2025)
            color: 0x4fd0e7,
            trailColor: 0x00bcd4,
            albedo: 0.3,
            surfaceGravity: 8.69, // m/s²
            atmosphereComposition: '83% H₂, 15% He, 2% CH₄',
            moons: 27,
            hasRings: true,
            axialTilt: 97.77, // grados (rotación casi lateral)
        },
        {
            id: 'neptune',
            name: 'Neptuno',
            radius: 24622, // km (radio ecuatorial)
            mass: 1.024e26, // kg
            orbitalRadius: 4514.953e6, // km (30.178 AU) - actualizado 2025
            orbitalPeriod: 60189.018, // días (164.79 años) - actualizado 2025
            rotationPeriod: 0.6713, // días (16h 6m)
            orbitalEccentricity: 0.0097, // actualizado 2025
            orbitalInclination: 1.77, // grados (actualizado 2025)
            color: 0x4b70dd,
            trailColor: 0x3f51b5,
            albedo: 0.29,
            surfaceGravity: 11.15, // m/s²
            atmosphereComposition: '80% H₂, 19% He, 1% CH₄',
            moons: 16,
            hasRings: true,
            windSpeed: 2100, // km/h (vientos más rápidos del sistema solar)
        },
    ],
};

/**
 * Función para obtener datos de un planeta específico
 * @param {string} planetId - ID del planeta
 * @returns {Object|null} Datos del planeta o null si no existe
 */
export function getPlanetData(planetId) {
    return solarSystemData.planets.find((planet) => planet.id === planetId) || null;
}

/**
 * Función para obtener todos los planetas ordenados por distancia al sol
 * @returns {Array} Array de planetas ordenados
 */
export function getPlanetsByDistance() {
    return [...solarSystemData.planets].sort((a, b) => a.orbitalRadius - b.orbitalRadius);
}

/**
 * Calcula la distancia orbital proporcional de un planeta
 * @param {number} realOrbitalRadius - Radio orbital real en km
 * @returns {number} Distancia orbital escalada para visualización
 */
export function calculateProportionalDistance(realOrbitalRadius) {
    const config = SCALE_CONFIG.proportionalSizing;

    if (!config.enabled) {
        // Usar el sistema de escalado anterior si está deshabilitado
        return realOrbitalRadius * SCALE_CONFIG.distanceScale;
    }

    // Calcular distancia proporcional basada en la Tierra como referencia
    const proportionalDistance =
        (realOrbitalRadius / config.baseOrbitalRadius) * config.baseOrbitalDistance;

    // Solo aplicar distancia mínima para Mercurio (evitar colisión con el sol)
    // Mantener proporciones reales para todos los demás planetas
    if (realOrbitalRadius < config.baseOrbitalRadius * 0.5) {
        // Solo planetas muy cercanos (Mercurio, Venus)
        return Math.max(proportionalDistance, config.minOrbitalDistance);
    }

    // Para el resto de planetas, usar proporciones exactas sin límites artificiales
    return proportionalDistance;
}

/**
 * Calcula el tamaño proporcional de un cuerpo celeste usando radios realistas
 * @param {number} realRadius - Radio real en km
 * @param {string} bodyType - Tipo de cuerpo ('sun' o 'planet')
 * @returns {number} Tamaño escalado para visualización (proporcional real)
 */
export function calculateProportionalSize(realRadius, _bodyType = 'planet') {
    const config = SCALE_CONFIG.proportionalSizing;

    if (!config.enabled) {
        // Usar el sistema de escalado anterior si está deshabilitado
        return Math.max(realRadius * SCALE_CONFIG.sizeScale, SCALE_CONFIG.minPlanetSize);
    }

    // Escala ÚNICA por radio real para TODOS los cuerpos (Sol y planetas). Así los
    // tamaños RELATIVOS son exactos: el Sol mide ~109x la Tierra y ~10x Júpiter, igual
    // que en la realidad. `baseSize` es el factor de magnificación global —el mismo para
    // todos— que hace visibles los planetas SIN distorsionar las proporciones.
    //
    // (Antes el Sol era un caso especial: sunRadiusFromOrbitalRatio lo encogía ~47x para
    // alinearlo con la escala de DISTANCIAS, pero eso rompía el ratio Sol/planeta y hacía
    // que Júpiter se renderizara MÁS GRANDE que el Sol. Ahora el Sol se escala por su radio
    // como cualquier planeta, conservando el ratio real.)
    //
    // Sin tamaños mínimos artificiales: los planetas pequeños son pequeños, como en la
    // realidad. El parámetro bodyType se mantiene por compatibilidad de la API.
    return (realRadius / config.baseRadius) * config.baseSize;
}

/**
 * Obtiene las proporciones relativas entre todos los cuerpos celestes
 * @returns {Object} Objeto con las proporciones calculadas
 */
export function getCelestialBodyProportions() {
    const proportions = {
        sun: {
            realRadius: solarSystemData.sun.radius,
            scaledSize: calculateProportionalSize(solarSystemData.sun.radius, 'sun'),
            sizeRatio: 1, // El sol como referencia base
        },
        planets: {},
    };

    // Calcular el ratio del sol para usar como referencia
    const sunScaledSize = proportions.sun.scaledSize;

    // Calcular proporciones para cada planeta
    solarSystemData.planets.forEach((planet) => {
        const scaledSize = calculateProportionalSize(planet.radius, 'planet');
        const scaledDistance = calculateProportionalDistance(planet.orbitalRadius);

        proportions.planets[planet.id] = {
            name: planet.name,
            realRadius: planet.radius,
            scaledSize: scaledSize,
            realOrbitalRadius: planet.orbitalRadius,
            scaledDistance: scaledDistance,
            sizeRatio: scaledSize / sunScaledSize, // Ratio respecto al sol
            realSizeRatio: planet.radius / solarSystemData.sun.radius, // Ratio real respecto al sol
            distanceRatio: scaledDistance / SCALE_CONFIG.proportionalSizing.baseOrbitalDistance,
            realDistanceRatio:
                planet.orbitalRadius / SCALE_CONFIG.proportionalSizing.baseOrbitalRadius,
        };
    });

    return proportions;
}

/**
 * Valida que las proporciones sean correctas entre cuerpos celestes
 * @returns {Object} Resultado de la validación de proporciones
 */
export function validateProportions() {
    const proportions = getCelestialBodyProportions();
    const errors = [];
    const warnings = [];

    // Verificar que los planetas más grandes tengan tamaños escalados mayores
    const planetsByRealSize = [...solarSystemData.planets].sort((a, b) => b.radius - a.radius);
    const planetsByScaledSize = [...solarSystemData.planets].sort(
        (a, b) => proportions.planets[b.id].scaledSize - proportions.planets[a.id].scaledSize
    );

    // Verificar que el orden se mantenga para los planetas principales
    const majorPlanets = [
        'jupiter',
        'saturn',
        'neptune',
        'uranus',
        'earth',
        'venus',
        'mars',
        'mercury',
    ];
    for (let i = 0; i < majorPlanets.length - 1; i++) {
        const current = majorPlanets[i];
        const next = majorPlanets[i + 1];

        if (proportions.planets[current] && proportions.planets[next]) {
            const currentSize = proportions.planets[current].scaledSize;
            const nextSize = proportions.planets[next].scaledSize;
            const currentReal = proportions.planets[current].realRadius;
            const nextReal = proportions.planets[next].realRadius;

            if (currentReal > nextReal && currentSize <= nextSize) {
                warnings.push(`Proporción invertida: ${current} debería ser mayor que ${next}`);
            }
        }
    }

    // Verificar que el sol sea visible pero no excesivamente grande
    const sunSize = proportions.sun.scaledSize;
    const largestPlanet = Math.max(...Object.values(proportions.planets).map((p) => p.scaledSize));

    if (sunSize < largestPlanet) {
        warnings.push('El sol escalado es menor que el planeta más grande escalado');
    }

    if (sunSize > largestPlanet * 100) {
        warnings.push('El sol escalado es excesivamente grande comparado con los planetas');
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        proportions,
    };
}

/**
 * Verifica si está activo el modo realista (siempre true)
 * @returns {boolean} True - siempre usa proporciones reales
 */
export function isRealisticMode() {
    return true; // Siempre proporciones reales
}

/**
 * Calcula las proporciones reales entre el sol y los planetas
 * @returns {Object} Ratios reales de tamaño
 */
export function getRealSizeRatios() {
    const sunRadius = solarSystemData.sun.radius;
    const ratios = {};

    solarSystemData.planets.forEach((planet) => {
        ratios[planet.id] = {
            name: planet.name,
            realRatio: sunRadius / planet.radius,
            scaledRatio:
                calculateProportionalSize(sunRadius, 'sun') /
                calculateProportionalSize(planet.radius, 'planet'),
        };
    });

    return ratios;
}

/**
 * Función para validar la integridad de los datos astronómicos
 * @returns {Object} Resultado de la validación
 */
export function validateAstronomicalData() {
    const errors = [];
    const warnings = [];

    // Validar datos del sol
    if (!solarSystemData.sun.radius || solarSystemData.sun.radius <= 0) {
        errors.push('Radio solar inválido');
    }

    // Validar cada planeta
    solarSystemData.planets.forEach((planet, index) => {
        if (!planet.id || !planet.name) {
            errors.push(`Planeta ${index}: ID o nombre faltante`);
        }

        if (!planet.radius || planet.radius <= 0) {
            errors.push(`${planet.name}: Radio inválido`);
        }

        if (!planet.orbitalRadius || planet.orbitalRadius <= 0) {
            errors.push(`${planet.name}: Radio orbital inválido`);
        }

        if (!planet.orbitalPeriod || planet.orbitalPeriod <= 0) {
            errors.push(`${planet.name}: Período orbital inválido`);
        }

        // Verificar que los planetas estén ordenados por distancia
        if (index > 0) {
            const prevPlanet = solarSystemData.planets[index - 1];
            if (planet.orbitalRadius <= prevPlanet.orbitalRadius) {
                warnings.push(`${planet.name}: Posible error en orden de distancia`);
            }
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        planetCount: solarSystemData.planets.length,
    };
}
